import React, { useState, useCallback, useMemo, useEffect } from "react";
import * as dateFns from "date-fns";
import CalendarHeader from "@/pages/attendance/views/calender/CalendarHeader";
import CalendarGrid from "@/pages/attendance/views/calender/CalendarGrid";
import { CardWithActions, Legend, LineDivider } from "@/components";
import AttendanceInfoModal from "@/pages/attendance/views/calender/AttendanceInfoModal";
import { legendItems } from "@/pages/attendance/views/calender/Legend";
import { FormControl, Select, MenuItem, Grid, Box } from "@mui/material";
import CardContent from "../components/CardContent";
import useFetchCommonData from "@/hooks/useFetchCommonData";
import { useGetCalendarQuery, useGetPayrollLockDateInfoQuery, useGetPayrollLockDateQuery } from "@/api/services/attendance/calendar/Calendar";
import { format, addMonths, subMonths, isBefore, isEqual, startOfDay } from "date-fns";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import mapOptions from "@/utils/mappers/mapOptions";


const Calendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [yearMonth, setYearMonth] = useState(format(new Date(), "MM-yyyy"));
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [filterData, setFilterData] = useState({ empId: "" });
  const { data: fetchEmployees } = useFetchCommonData("ALLEMPLOYEES");

  const { data: lockDate, refetch: refetchLockInfo } = useGetPayrollLockDateQuery({ yearMonth: yearMonth });

  const getLockMonth = useMemo(() => {
    // if (!lockDate?.data) return null;
    if (!lockDate?.data?.payrollMonth) return yearMonth;
    const { startDate, endDate } = lockDate.data.payrollMonth;
    const end = startOfDay(new Date(endDate));
    const presentDate = startOfDay(new Date(currentMonth));
    const nextMonth = new Date(currentMonth);
    nextMonth.setDate(1);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const currentLockMonth = format(presentDate, "MM-yyyy");
    const nextLockMonth = format(nextMonth, "MM-yyyy");
    // return presentDate >= end ? currentLockMonth : nextLockMonth;
    return isBefore(presentDate, end) || isEqual(presentDate, end)
        ? currentLockMonth
        : nextLockMonth;
  }, [lockDate, currentMonth, yearMonth]);
  const { data: isLocked, refetch: refetchPayrollLockDate } = useGetPayrollLockDateInfoQuery({ yearMonth: getLockMonth },
    {
      skip: !getLockMonth
    }
  );
  const lockDateRecords = isLocked?.data || [];

  const localStorageData = JSON.parse(localStorage.getItem("loginDetails"));
  const userDetails = localStorageData?.userDetails || {};
  const empId = filterData.empId || userDetails?.empId;
  const { data: data, refetch: refetchCalendar, isUninitialized } = useGetCalendarQuery(
    { empIds: empId, dateObj: getLockMonth },
    { skip: !getLockMonth }
  );
  const attendanceRecords = data?.data || [];


  useEffect(() => {
    if (!isUninitialized) {
      refetchCalendar();
    }
  }, [filterData.empId, refetchCalendar, isUninitialized]);

  useEffect(() => {
    if (getLockMonth && empId) {
      refetchCalendar();
    }
  }, [empId, getLockMonth]);

  const handlePrevMonth = () => {

    let monthYear = lockDateRecords?.monthYear;
    if (!monthYear) return;
    const [month, year] = monthYear.split("-").map(Number);
    const val = new Date(year, month - 1, 1);
    const newMonth = subMonths(val, 1);
    // const newMonth = subMonths(currentMonth, 1);
    const updatedMonth = format(newMonth, "MM-yyyy");
    setCurrentMonth(newMonth);
    setYearMonth(updatedMonth);
    refetchPayrollLockDate();
    refetchLockInfo();
    refetchCalendar();
  };

  const handleNextMonth = () => {

    let monthYear = lockDateRecords?.monthYear;
    if (!monthYear) return;
    const [month, year] = monthYear.split("-").map(Number);
    const val = new Date(year, month - 1, 1);
    const newMonth = addMonths(val, 1); 
    // const newMonth = addMonths(currentMonth, 1);
    const updatedMonth = format(newMonth, "MM-yyyy");
    setCurrentMonth(newMonth);
    setYearMonth(updatedMonth);
    refetchPayrollLockDate();
    refetchLockInfo();
    refetchCalendar();
  };

  const handleDayClick = useCallback((day) => {
    setSelectedDay(day);
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const formatMonthYear = (monthYear) => {
    if (!monthYear) return ""; // Handle null/undefined case
    // Split "03-2025" into [month, year]
    const [month, year] = monthYear.split("-").map(Number);
    // Construct a Date object using the first day of the month
    const date = new Date(year, month - 1, 1); // month - 1 since JS months are 0-based
    // Format as "March 2025"
    return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" })?.format(date);
  };

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const filteredDataList =
    !filterData.empId
      ? attendanceRecords
      : attendanceRecords.filter((data) => String(data.empId) === String(filterData.empId));

  const attendanceStatus = (filteredDataList || []).reduce((acc, record) => {
    acc[record.attendanceDate] = {
      attendanceData: record.attendanceData,
      regularizationData: record.regularization,
      shiftData: record.shift,
      leaveData: record.leave,
    };
    return acc;
  }, {});

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilterData((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  }, []);

  const filterConfig = useMemo(
    () => [
      {
        name: "empId",
        type: "select",
        placeholder: "Select Employee",
        options: mapOptions(fetchEmployees || []),
        onChange: handleFilterChange,
      },
    ],
    [fetchEmployees, handleFilterChange]
  );

  return (
    <CardContent title="Attendance Info"
      actions={[

        <Box key="filters" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Formik initialValues={{ empId: filterData.empId }}>
            {({ setFieldValue }) => (
              <Form>
                <Grid container spacing={2} alignItems="center" sx={{ mb: "3px" }}>
                  {filterConfig.map((field) => (
                    <Grid
                      key={field.name}
                      item
                    >
                      {/* Render Select Inputs */}
                      {field.type === "select" && (
                        <FormikSelectInput
                          name={field.name}
                          sx={{
                            width: "280px",
                            height: "40px",
                            marginBottom: "0px",
                          }}
                          truncateLength={30}
                          options={field.options || []}
                          placeholder={field.placeholder || `Select ${field.label}`}
                          disabled={field.disable || false}
                          required={field.required || false}
                          search={true}
                          onChange={(event) => {
                            const value = event.target.value;
                            const name = field.name;
                            setFieldValue(name, value); // Update Formik state
                            // Call your custom onChange with correct name + value
                            field.onChange?.({
                              target: {
                                name,
                                value,
                              },
                            });
                          }}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Form>
            )}
          </Formik>
          <Legend
            triggerLabel="Legend"
            legendItems={legendItems}
            cardWidth={187}
            buttonHeight={40}
            key="legend"

          />
        </Box>
      ]}>
      <CalendarHeader
        title={formatMonthYear(lockDateRecords?.monthYear)}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
      />
      <CalendarGrid
        lockDateRecords={lockDateRecords}
        onDayClick={handleDayClick}
        attendanceStatus={attendanceStatus}
        showData={filterData.empId !== ""}
        yearMonth={yearMonth}
      />

      {selectedDay && (
        <AttendanceInfoModal
          attendanceInfotoggleOpen={openDialog}
          attendanceInfotoggleClose={handleCloseDialog}
          addItemViewRemark={() => { }}
          // empId={selectedEmpId}
          empId={empId}
          date={formatDate(new Date(selectedDay))}
          day={new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(
            new Date(selectedDay)
          )}
          shift="Shift 1"
          InfoData={attendanceRecords}
          title={`Attendance Details for ${formatDate(new Date(selectedDay))}`}
        />
      )}
    </CardContent>
  );
};

export default Calendar;
